import React from 'react';
import { OnboardingData } from './OnboardingFlow';

interface OnboardingPreferencesProps {
  data: OnboardingData;
  updateData: (updates: Partial<OnboardingData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const OnboardingPreferences: React.FC<OnboardingPreferencesProps> = ({
  data,
  updateData,
  onNext,
  onBack,
}) => {
  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Stay Connected</h2>
      <p style={styles.subtext}>
        Choose how you'd like to hear from us. You can change these anytime.
      </p>

      <div style={styles.options}>
        <div
          style={{
            ...styles.optionCard,
            borderColor: data.subscribe_to_reminders ? '#508590' : '#e0e0e0',
            backgroundColor: data.subscribe_to_reminders ? '#f0f7f8' : '#fff',
          }}
          onClick={() => updateData({ subscribe_to_reminders: !data.subscribe_to_reminders })}
        >
          <div style={styles.optionHeader}>
            <div style={styles.optionIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
            <div style={styles.checkbox}>
              {data.subscribe_to_reminders && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
          </div>
          <h3 style={styles.optionTitle}>Feed Reminders</h3>
          <p style={styles.optionText}>
            Get notified when you're running low on feed based on your animals' consumption. We'll calculate when
            you'll need to restock.
          </p>
        </div>

        <div
          style={{
            ...styles.optionCard,
            borderColor: data.subscribe_to_weather_alerts ? '#508590' : '#e0e0e0',
            backgroundColor: data.subscribe_to_weather_alerts ? '#f0f7f8' : '#fff',
          }}
          onClick={() => updateData({ subscribe_to_weather_alerts: !data.subscribe_to_weather_alerts })}
        >
          <div style={styles.optionHeader}>
            <div style={styles.optionIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
            </div>
            <div style={styles.checkbox}>
              {data.subscribe_to_weather_alerts && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
          </div>
          <h3 style={styles.optionTitle}>Weather Alerts</h3>
          <p style={styles.optionText}>
            Receive alerts when dangerous weather conditions are forecast for your area, tailored to the animals you have.
          </p>
        </div>
      </div>

      {/* Price Sheet Prompt */}
      <div style={{
        padding: '1.25rem', borderRadius: '12px',
        border: '2px solid #e5e7eb', backgroundColor: '#fefce8',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            backgroundColor: '#eab308', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#333', margin: '0 0 0.25rem', fontFamily: "'Open Sans', sans-serif" }}>
              Got a price sheet?
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#666', margin: 0, lineHeight: '1.5' }}>
              After signup, you can upload your farm price sheet in <strong>My Documents</strong>.
              Laura will learn your prices and automatically track sales revenue from your voice notes.
            </p>
          </div>
        </div>
      </div>

      <div style={styles.infoBox}>
        <div style={styles.infoIcon}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>
        <p style={styles.infoText}>
          We'll only send emails when they're helpful to you. No spam, ever. You can unsubscribe at any time.
        </p>
      </div>

      <div style={styles.actions}>
        <button style={styles.backButton} onClick={onBack}>
          Back
        </button>
        <button style={styles.nextButton} onClick={onNext}>
          Continue
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
    fontFamily: "'Open Sans', sans-serif",
  },
  subtext: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
    lineHeight: '1.5',
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  optionCard: {
    padding: '1.25rem',
    borderRadius: '12px',
    border: '2px solid #e0e0e0',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  optionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.75rem',
  },
  optionIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: '#508590',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkbox: {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    backgroundColor: '#508590',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 0.5rem 0',
    fontFamily: "'Open Sans', sans-serif",
  },
  optionText: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
    lineHeight: '1.5',
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '1rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
  },
  infoIcon: {
    color: '#666',
    flexShrink: 0,
    marginTop: '2px',
  },
  infoText: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
    lineHeight: '1.5',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  backButton: {
    flex: 1,
    backgroundColor: 'transparent',
    color: '#666',
    border: '2px solid #e0e0e0',
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  nextButton: {
    flex: 2,
    backgroundColor: '#508590',
    color: '#fff',
    border: 'none',
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
};

export default OnboardingPreferences;
